import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Check, AlertTriangle, Eye, EyeOff, Trash2 } from 'lucide-react';
import trainingApi from '../api';
import PptxUploadBox from './PptxUploadBox';
import QuestionEditor from './QuestionEditor';
import TranslationsEditor from './TranslationsEditor';
import { LANGUAGES, labelFor, nativeLabelFor } from '../i18n/strings';

const TrainingEditor = () => {
  const { trainingId } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [languageRows, setLanguageRows] = useState([]);
  const [translations, setTranslations] = useState({});
  const [activeLanguage, setActiveLanguage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await trainingApi.adminGet(trainingId);
      setTraining(data.training);
      setQuestions(data.questions);
      setLanguageRows(data.languages || []);
      setTranslations(data.translations || {});
      setActiveLanguage((cur) => cur || data.training.base_language || 'en');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [trainingId]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setTraining((t) => ({ ...t, [k]: v }));

  const baseLanguage = training?.base_language || 'en';
  const languageRowFor = useCallback(
    (code) => languageRows.find((r) => r.language === code),
    [languageRows]
  );

  // Per-language readiness, used for the tab badges: a language is only worth showing
  // to trainees once it has both a deck and its quiz text.
  const statusByLanguage = useMemo(() => {
    const out = {};
    for (const { code } of LANGUAGES) {
      const row = languageRowFor(code);
      if (code === baseLanguage) {
        out[code] = {
          slideCount: row ? row.slide_count : training?.slide_count || 0,
          translated: questions.length,
          total: questions.length,
          available: true,
          exists: true,
        };
        continue;
      }
      const translated = questions.filter((q) => (q.translations?.[code] || '').trim()).length;
      out[code] = {
        slideCount: row ? row.slide_count : 0,
        translated,
        total: questions.length,
        available: !!row?.is_available,
        exists: !!row,
      };
    }
    return out;
  }, [languageRowFor, baseLanguage, questions, training]);

  const save = async () => {
    setSaving(true);
    setError('');
    setOkMsg('');
    try {
      await trainingApi.adminUpdate(trainingId, {
        title: training.title,
        description: training.description,
        instructions_md: training.instructions_md,
        pass_threshold_pct: Number(training.pass_threshold_pct),
        max_retries: training.max_retries === '' ? null : (training.max_retries === null ? null : Number(training.max_retries)),
        reveal_wrong_answers: !!training.reveal_wrong_answers,
        randomize_questions: !!training.randomize_questions,
        time_limit_seconds: training.time_limit_seconds === '' ? null : (training.time_limit_seconds === null ? null : Number(training.time_limit_seconds)),
        is_published: !!training.is_published,
      });
      setOkMsg('Saved.');
      setTimeout(() => setOkMsg(''), 2000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async (code, next) => {
    setError('');
    try {
      await trainingApi.adminSetLanguageAvailability(trainingId, code, next);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const removeLanguage = async (code) => {
    const label = labelFor(code);
    if (!window.confirm(
      `Remove the ${label} version?\n\nThis deletes the ${label} slides and all ${label} question text for this training. Attempts already taken in ${label} keep their history. This cannot be undone.`
    )) return;
    setError('');
    try {
      await trainingApi.adminDeleteLanguage(trainingId, code);
      setActiveLanguage(baseLanguage);
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (!training) return <div className="p-8 text-rose-300">{error || 'Not found'}</div>;

  const activeStatus = statusByLanguage[activeLanguage] || {};
  const activeRow = languageRowFor(activeLanguage);
  const isBase = activeLanguage === baseLanguage;

  return (
    <div className="p-8 max-w-4xl">
      <button
        type="button"
        onClick={() => navigate('/tiles/ksp_training/admin/trainings')}
        className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Trainings
      </button>

      <h1 className="text-2xl font-bold mb-1">Edit Training</h1>
      <p className="text-slate-400 text-sm mb-6">
        One training, one quiz, one set of results — with a language version per audience.
      </p>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">{error}</div>
      )}
      {okMsg && (
        <div className="mb-4 p-3 rounded bg-emerald-900/40 border border-emerald-700 text-emerald-200">{okMsg}</div>
      )}

      {/* Language tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-700 pb-3">
        {LANGUAGES.map(({ code }) => {
          const status = statusByLanguage[code] || {};
          const isActive = code === activeLanguage;
          const complete = status.total > 0 && status.translated === status.total && status.slideCount > 0;
          return (
            <button
              key={code}
              type="button"
              onClick={() => setActiveLanguage(code)}
              className={`px-4 py-2 rounded-t-md text-sm border-b-2 transition-colors ${
                isActive
                  ? 'border-amber-500 text-amber-300 bg-slate-800'
                  : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <span className="font-medium">{nativeLabelFor(code)}</span>
              {code === baseLanguage && <span className="ml-1 text-xs opacity-70">(base)</span>}
              <span className="ml-2 text-xs">
                {code === baseLanguage ? (
                  <StatusDot ok={status.slideCount > 0} />
                ) : !status.exists ? (
                  <span className="text-slate-500">not added</span>
                ) : (
                  <>
                    <StatusDot ok={complete} />
                    {!status.available && <span className="ml-1 text-slate-500">hidden</span>}
                  </>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {isBase ? (
        <>
          <div className="rounded-lg border border-slate-700 bg-slate-800 p-4 mb-6 space-y-4">
            <p className="text-xs text-slate-400">
              These settings apply to every language. Scores, retries and completion records
              are shared across all versions of this training.
            </p>

            <Field label="Title">
              <input
                type="text"
                value={training.title || ''}
                onChange={(e) => setField('title', e.target.value)}
                className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
              />
            </Field>
            <Field label="Description">
              <textarea
                rows={2}
                value={training.description || ''}
                onChange={(e) => setField('description', e.target.value)}
                className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Passing Score (%)">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={training.pass_threshold_pct ?? 80}
                  onChange={(e) => setField('pass_threshold_pct', e.target.value)}
                  className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
                />
              </Field>
              <Field label="Max Retries (blank = unlimited)">
                <input
                  type="number"
                  min={0}
                  value={training.max_retries ?? ''}
                  onChange={(e) => setField('max_retries', e.target.value === '' ? null : e.target.value)}
                  className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
                />
              </Field>
              <Field label="Time Limit (seconds, blank = none)">
                <input
                  type="number"
                  min={0}
                  value={training.time_limit_seconds ?? ''}
                  onChange={(e) => setField('time_limit_seconds', e.target.value === '' ? null : e.target.value)}
                  className="w-full px-3 py-2 rounded bg-slate-900 border border-slate-700 text-slate-100"
                />
              </Field>
              <Field label=" ">
                <div className="flex flex-col gap-2">
                  <Toggle
                    label="Reveal wrong answers after submit"
                    checked={!!training.reveal_wrong_answers}
                    onChange={(v) => setField('reveal_wrong_answers', v)}
                  />
                  <Toggle
                    label="Randomize question order"
                    checked={!!training.randomize_questions}
                    onChange={(v) => setField('randomize_questions', v)}
                  />
                  <Toggle
                    label="Published (visible to users)"
                    checked={!!training.is_published}
                    onChange={(v) => setField('is_published', v)}
                  />
                </div>
              </Field>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white flex items-center gap-2 disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <PptxUploadBox
              trainingId={training.id}
              language={baseLanguage}
              currentSlideCount={activeStatus.slideCount}
              sourceFilename={activeRow?.source_filename}
              onUploaded={() => load()}
            />
          </div>

          <QuestionEditor
            trainingId={training.id}
            questions={questions}
            onChange={load}
          />
        </>
      ) : (
        <div className="space-y-6">
          {/* Visibility control for this language version */}
          <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-semibold mb-1">
                  {labelFor(activeLanguage)} version
                </h3>
                <p className="text-xs text-slate-400 max-w-lg">
                  {activeStatus.available
                    ? `Trainees can choose ${nativeLabelFor(activeLanguage)} for this training.`
                    : `Hidden: trainees who pick ${nativeLabelFor(activeLanguage)} get the ${labelFor(baseLanguage)} version instead. Turn this on once the translation has been reviewed.`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => toggleAvailability(activeLanguage, !activeStatus.available)}
                  className={`px-3 py-2 rounded text-sm flex items-center gap-2 ${
                    activeStatus.available
                      ? 'bg-slate-700 hover:bg-slate-600 text-slate-100'
                      : 'bg-emerald-700 hover:bg-emerald-600 text-white'
                  }`}
                >
                  {activeStatus.available
                    ? <><EyeOff className="w-4 h-4" /> Hide from trainees</>
                    : <><Eye className="w-4 h-4" /> Make available</>}
                </button>
                {activeStatus.exists && (
                  <button
                    type="button"
                    onClick={() => removeLanguage(activeLanguage)}
                    className="px-3 py-2 rounded text-sm bg-rose-900/60 hover:bg-rose-800 text-rose-200 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Remove
                  </button>
                )}
              </div>
            </div>

            {activeStatus.slideCount === 0 && (
              <div className="mt-3 p-2 rounded bg-amber-900/30 border border-amber-700 text-amber-200 text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                No {labelFor(activeLanguage)} deck yet — trainees in this language would see the
                {` ${labelFor(baseLanguage)}`} slides.
              </div>
            )}
          </div>

          <PptxUploadBox
            trainingId={training.id}
            language={activeLanguage}
            currentSlideCount={activeStatus.slideCount}
            sourceFilename={activeRow?.source_filename}
            onUploaded={() => load()}
          />

          <TranslationsEditor
            trainingId={training.id}
            language={activeLanguage}
            baseLanguage={baseLanguage}
            course={training}
            translations={translations}
            questions={questions}
            onSaved={load}
          />
        </div>
      )}
    </div>
  );
};

const StatusDot = ({ ok }) => (
  ok
    ? <Check className="w-3.5 h-3.5 inline text-emerald-400" />
    : <AlertTriangle className="w-3.5 h-3.5 inline text-amber-400" />
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="text-sm">{label}</span>
  </label>
);

export default TrainingEditor;
